import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    ChevronDown, ChevronRight, Edit, Trash2,
    Search, Calendar, Clock, Heart, Upload, X, Download, Share2, Eye,
    Star, ChefHat, Utensils, Filter, Tag, BookOpen, Award, Flame, Leaf,
    Grid, List, Copy, Printer, MessageCircle, ThumbsUp, AlertCircle, Check,
    Plus, Crown
} from 'lucide-react';
import { fetchRecipes, createRecipe, updateRecipe, deleteRecipe } from '../services/recipesApi';
import MemberAvatar from './MemberAvatar';
import UserAvatar from './UserAvatar';
import MemberSelector from './MemberSelector';
import Premium from './Premium';
import NavBar from './NavBar';

const RecipeBox = () => {
    const navigate = useNavigate();
    const [viewMode, setViewMode] = useState('grid'); // grid or list
    const [showPremiumModal, setShowPremiumModal] = useState(false);
    const [families, setFamilies] = useState([]);
    const [selectedFamily, setSelectedFamily] = useState(null);
    const photoInputRef = useRef(null);

    // Recipes state
    const [recipes, setRecipes] = useState([]);
    const [selectedRecipe, setSelectedRecipe] = useState(null);
    const [showAddRecipeModal, setShowAddRecipeModal] = useState(false);
    const [showRecipeDetailModal, setShowRecipeDetailModal] = useState(false);
    const [editingRecipe, setEditingRecipe] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterCuisine, setFilterCuisine] = useState('All');
    const [filterMealType, setFilterMealType] = useState('All');
    const [filterDifficulty, setFilterDifficulty] = useState('All');
    const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);

    const [newRecipe, setNewRecipe] = useState({
        title: '',
        description: '',
        ingredients: [{ name: '', quantity: '', unit: '', optional: false, notes: '' }],
        instructions: [''],
        prepTime: '',
        cookTime: '',
        servings: '',
        cuisineType: 'Indian',
        difficulty: 'Medium',
        tags: [],
        nutritionInfo: {
            calories: '',
            protein: '',
            carbs: '',
            fats: '',
            allergens: ''
        },
        mealType: 'Dinner',
        isFamilyFavorite: false,
        isHealthy: false,
        isTraditional: false,
        status: 'published',
        photoUrl: null,
        attachments: [],
        recipeOrigin: '',
        healthySwaps: '',
        createdBy: 'Current User'
    });

    // Meal Planner state
    const [mealPlan, setMealPlan] = useState({});
    const [showMealPlanModal, setShowMealPlanModal] = useState(false);
    const [selectedDate, setSelectedDate] = useState('');
    const [selectedMeal, setSelectedMeal] = useState('');

    // Load data
    useEffect(() => {
        loadFamilies();
        loadMealPlan();
    }, []);

    // Load recipes when family changes
    useEffect(() => {
        if (selectedFamily) {
            loadRecipes();
        }
    }, [selectedFamily]);

    const loadFamilies = async () => {
        try {
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
            const response = await fetch(`${apiUrl}/v1/families`, {
                credentials: 'include'
            });
            
            if (!response.ok) {
                throw new Error('Failed to load families');
            }
            
            const familiesData = await response.json();
            setFamilies(familiesData);

            const selectedFamilyId = localStorage.getItem('selectedFamilyId');
            let familyToSelect = null;
            
            if (selectedFamilyId && familiesData.length > 0) {
                familyToSelect = familiesData.find(f => f.id === selectedFamilyId);
            }
            
            if (!familyToSelect && familiesData.length === 1) {
                familyToSelect = familiesData[0];
                localStorage.setItem('selectedFamilyId', familiesData[0].id);
            }
            
            if (!familyToSelect && familiesData.length > 0) {
                localStorage.removeItem('selectedFamilyId');
            }
            
            setSelectedFamily(familyToSelect);
        } catch (error) {
            console.error('Error loading families:', error);
        }
    };

    const loadRecipes = async () => {
        if (!selectedFamily) return;
        
        try {
            const response = await fetchRecipes(selectedFamily.id);
            setRecipes(response.items || []);
        } catch (error) {
            console.error('Error loading recipes:', error);
        }
    };

    const loadMealPlan = () => {
        const stored = localStorage.getItem('mealPlan');
        if (stored) {
            setMealPlan(JSON.parse(stored));
        }
    };

    // Removed saveRecipes - now using API directly

    const saveMealPlan = (data) => {
        localStorage.setItem('mealPlan', JSON.stringify(data));
        setMealPlan(data);
    };

    const handleLogout = () => {
        localStorage.removeItem('hasLoggedIn');
        localStorage.removeItem('selectedFamilyId');
        navigate('/login');
    };

    // Photo handling
    const handlePhotoSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                alert('Photo size must be less than 5MB');
                return;
            }

            const reader = new FileReader();
            reader.onloadend = () => {
                setNewRecipe({ ...newRecipe, photoUrl: reader.result });
            };
            reader.readAsDataURL(file);
        }
    };

    // Recipe CRUD
    const handleAddRecipe = async () => {
        if (!newRecipe.title.trim()) {
            alert('Please enter recipe title');
            return;
        }

        if (!selectedFamily) {
            alert('Please select a family first');
            return;
        }

        try {
            // Map frontend fields to backend schema
            const recipeData = {
                name: newRecipe.title,
                description: newRecipe.description || null,
                ingredients: newRecipe.ingredients.map(i => ({
                    name: i.name,
                    quantity: i.quantity,
                    unit: i.unit,
                    optional: i.optional || false
                })),
                instructions: newRecipe.instructions.join('\n'),
                prep_time_minutes: parseInt(newRecipe.prepTime) || null,
                cook_time_minutes: parseInt(newRecipe.cookTime) || null,
                servings: parseInt(newRecipe.servings) || null,
                cuisine: newRecipe.cuisineType || null,
                tags: newRecipe.tags || [],
                photo_url: newRecipe.photoUrl || null
            };

            const createdRecipe = await createRecipe(selectedFamily.id, recipeData);
            setRecipes([...recipes, createdRecipe]);
            resetRecipeForm();
            setShowAddRecipeModal(false);
        } catch (error) {
            console.error('Error creating recipe:', error);
            alert(`Failed to create recipe: ${error.message}`);
        }
    };

    const handleUpdateRecipe = async () => {
        if (!newRecipe.title.trim()) {
            alert('Please enter recipe title');
            return;
        }

        if (!selectedFamily) {
            alert('Please select a family first');
            return;
        }

        try {
            // Map frontend fields to backend schema
            const recipeData = {
                name: newRecipe.title,
                description: newRecipe.description || null,
                ingredients: newRecipe.ingredients.map(i => ({
                    name: i.name,
                    quantity: i.quantity,
                    unit: i.unit,
                    optional: i.optional || false
                })),
                instructions: newRecipe.instructions.join('\n'),
                prep_time_minutes: parseInt(newRecipe.prepTime) || null,
                cook_time_minutes: parseInt(newRecipe.cookTime) || null,
                servings: parseInt(newRecipe.servings) || null,
                cuisine: newRecipe.cuisineType || null,
                tags: newRecipe.tags || [],
                photo_url: newRecipe.photoUrl || null
            };

            const updatedRecipe = await updateRecipe(selectedFamily.id, editingRecipe.id, recipeData);
            const updated = recipes.map(r => r.id === editingRecipe.id ? updatedRecipe : r);
            setRecipes(updated);
            resetRecipeForm();
            setShowAddRecipeModal(false);
            setEditingRecipe(null);
        } catch (error) {
            console.error('Error updating recipe:', error);
            alert(`Failed to update recipe: ${error.message}`);
        }
    };

    const handleDeleteRecipe = async (id) => {
        if (!window.confirm('Are you sure you want to delete this recipe?')) {
            return;
        }

        if (!selectedFamily) {
            alert('Please select a family first');
            return;
        }

        try {
            await deleteRecipe(selectedFamily.id, id);
            setRecipes(recipes.filter(r => r.id !== id));
            if (selectedRecipe?.id === id) {
                setSelectedRecipe(null);
                setShowRecipeDetailModal(false);
            }
        } catch (error) {
            console.error('Error deleting recipe:', error);
            alert(`Failed to delete recipe: ${error.message}`);
        }
    };

    const handleToggleFavorite = async (id) => {
        if (!selectedFamily) return;
        
        const recipe = recipes.find(r => r.id === id);
        if (!recipe) return;
        
        try {
            const recipeData = {
                name: recipe.name || recipe.title,
                description: recipe.description || null,
                ingredients: recipe.ingredients || [],
                instructions: recipe.instructions || '',
                prep_time_minutes: recipe.prepTime || recipe.prep_time_minutes,
                cook_time_minutes: recipe.cookTime || recipe.cook_time_minutes,
                servings: recipe.servings,
                cuisine: recipe.cuisineType || recipe.cuisine,
                category: recipe.category || null,
                difficulty: recipe.difficulty || null,
                photo_url: recipe.photoUrl || recipe.photo_url || null,
                tags: recipe.tags || [],
                is_family_favorite: !recipe.isFamilyFavorite,
                created_by_member_id: recipe.createdBy || recipe.created_by_member_id || null
            };
            
            const updated = await updateRecipe(selectedFamily.id, id, recipeData);
            setRecipes(recipes.map(r => r.id === id ? updated : r));
        } catch (error) {
            console.error('Error toggling favorite:', error);
        }
    };

    const handleAddRating = async (recipeId, rating) => {
        if (!selectedFamily) return;
        
        const recipe = recipes.find(r => r.id === recipeId);
        if (!recipe) return;
        
        try {
            const newRatings = [...(recipe.ratings || []), { 
                user: 'Current User', 
                rating, 
                date: new Date().toISOString() 
            }];
            
            const recipeData = {
                name: recipe.name || recipe.title,
                description: recipe.description || null,
                ingredients: recipe.ingredients || [],
                instructions: recipe.instructions || '',
                prep_time_minutes: recipe.prepTime || recipe.prep_time_minutes,
                cook_time_minutes: recipe.cookTime || recipe.cook_time_minutes,
                servings: recipe.servings,
                cuisine: recipe.cuisineType || recipe.cuisine,
                category: recipe.category || null,
                difficulty: recipe.difficulty || null,
                photo_url: recipe.photoUrl || recipe.photo_url || null,
                tags: recipe.tags || [],
                is_family_favorite: recipe.isFamilyFavorite || false,
                created_by_member_id: recipe.createdBy || recipe.created_by_member_id || null,
                ratings: newRatings
            };
            
            const updated = await updateRecipe(selectedFamily.id, recipeId, recipeData);
            setRecipes(recipes.map(r => r.id === recipeId ? updated : r));
        } catch (error) {
            console.error('Error adding rating:', error);
        }
    };

    const resetRecipeForm = () => {
        setNewRecipe({
            title: '',
            description: '',
            ingredients: [{ name: '', quantity: '', unit: '', optional: false, notes: '' }],
            instructions: [''],
            prepTime: '',
            cookTime: '',
            servings: '',
            cuisineType: 'Indian',
            difficulty: 'Medium',
            tags: [],
            nutritionInfo: {
                calories: '',
                protein: '',
                carbs: '',
                fats: '',
                allergens: ''
            },
            mealType: 'Dinner',
            isFamilyFavorite: false,
            isHealthy: false,
            isTraditional: false,
            status: 'published',
            photoUrl: null,
            attachments: [],
            recipeOrigin: '',
            healthySwaps: '',
            createdBy: 'Current User'
        });
    };

    // Ingredient functions
    const addIngredient = () => {
        setNewRecipe({
            ...newRecipe,
            ingredients: [...newRecipe.ingredients, { name: '', quantity: '', unit: '', optional: false, notes: '' }]
        });
    };

    const removeIngredient = (index) => {
        const updated = newRecipe.ingredients.filter((_, i) => i !== index);
        setNewRecipe({ ...newRecipe, ingredients: updated });
    };

    const updateIngredient = (index, field, value) => {
        const updated = newRecipe.ingredients.map((ing, i) => 
            i === index ? { ...ing, [field]: value } : ing
        );
        setNewRecipe({ ...newRecipe, ingredients: updated });
    };

    // Instruction functions
    const addInstruction = () => {
        setNewRecipe({
            ...newRecipe,
            instructions: [...newRecipe.instructions, '']
        });
    };

    const removeInstruction = (index) => {
        const updated = newRecipe.instructions.filter((_, i) => i !== index);
        setNewRecipe({ ...newRecipe, instructions: updated });
    };

    const updateInstruction = (index, value) => {
        const updated = newRecipe.instructions.map((inst, i) => 
            i === index ? value : inst
        );
        setNewRecipe({ ...newRecipe, instructions: updated });
    };

    // Meal planning
    const handleAddToMealPlan = () => {
        if (!selectedDate || !selectedMeal) {
            alert('Please select date and meal type');
            return;
        }

        const key = `${selectedDate}_${selectedMeal}`;
        saveMealPlan({
            ...mealPlan,
            [key]: selectedRecipe.id
        });

        setShowMealPlanModal(false);
        alert('Recipe added to meal plan!');
    };

    const generateGroceryList = () => {
        const recipeIds = Object.values(mealPlan);
        const allIngredients = [];

        recipeIds.forEach(recipeId => {
            const recipe = recipes.find(r => r.id === recipeId);
            if (recipe) {
                allIngredients.push(...recipe.ingredients);
            }
        });

        // Group by ingredient name
        const grouped = allIngredients.reduce((acc, ing) => {
            if (!acc[ing.name]) {
                acc[ing.name] = [];
            }
            acc[ing.name].push(ing);
            return acc;
        }, {});

        return grouped;
    };

    // Export functions
    const handleExportRecipe = (recipe) => {
        alert(`Exporting recipe: ${recipe.title} as PDF... (PDF generation would happen here)`);
    };

    const handlePrintRecipe = (recipe) => {
        alert(`Printing recipe: ${recipe.title}... (Print dialog would open here)`);
    };

    // Filter recipes
    const filteredRecipes = recipes.filter(recipe => {
        const matchesSearch = recipe.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                              recipe.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                              recipe.ingredients.some(ing => ing.name.toLowerCase().includes(searchQuery.toLowerCase()));
        const matchesCuisine = filterCuisine === 'All' || recipe.cuisineType === filterCuisine;
        const matchesMealType = filterMealType === 'All' || recipe.mealType === filterMealType;
        const matchesDifficulty = filterDifficulty === 'All' || recipe.difficulty === filterDifficulty;
        const matchesFavorites = !showFavoritesOnly || recipe.isFamilyFavorite;

        return matchesSearch && matchesCuisine && matchesMealType && matchesDifficulty && matchesFavorites;
    });

    const cuisineTypes = ['Indian', 'Italian', 'Chinese', 'Mexican', 'Japanese', 'Thai', 'American', 'Mediterranean', 'Fusion', 'Other'];
    const mealTypes = ['Breakfast', 'Lunch', 'Dinner', 'Snack', 'Dessert', 'Beverage'];
    const difficulties = ['Easy', 'Medium', 'Hard'];
    const units = ['cup', 'tbsp', 'tsp', 'g', 'kg', 'ml', 'l', 'oz', 'lb', 'piece', 'pinch'];

    const getAverageRating = (recipe) => {
        if (!recipe.ratings || recipe.ratings.length === 0) return 0;
        const sum = recipe.ratings.reduce((acc, r) => acc + r.rating, 0);
        return (sum / recipe.ratings.length).toFixed(1);
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
            {/* Navigation Bar */}
            <NavBar
                families={families}
                selectedFamily={selectedFamily}
                setSelectedFamily={setSelectedFamily}
                setShowPremiumModal={setShowPremiumModal}
                handleLogout={handleLogout}
            />

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Breadcrumb */}
                <div className="mb-4 flex items-center gap-2 text-sm">
                    <button 
                        onClick={() => navigate('/dashboard')}
                        className="text-gray-600 hover:text-primary-blue transition-colors"
                    >
                        Dashboard
                    </button>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-900 font-medium">Recipe Box</span>
                </div>

                {/* Header */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                            <ChefHat className="w-10 h-10 text-amber-600" />
                            Recipe Box
                        </h1>
                        <p className="text-gray-600 mt-1">Family recipes and meal planning</p>
                    </div>
                    <button
                        onClick={() => setShowAddRecipeModal(true)}
                        className="flex items-center gap-2 px-6 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 font-medium shadow-md hover:shadow-lg transition-all"
                    >
                        <Plus className="w-5 h-5" />
                        Add Recipe
                    </button>
                </div>

                {/* Search and Filters */}
                <div className="bg-white rounded-lg shadow-md p-6 mb-6 space-y-4">
                    <div className="flex flex-col md:flex-row gap-4">
                        {/* Search */}
                        <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search recipes, ingredients..."
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                            />
                        </div>

                        {/* View Toggle */}
                        <div className="flex items-center gap-2 border border-gray-300 rounded-lg p-1">
                            <button
                                onClick={() => setViewMode('grid')}
                                className={`p-2 rounded ${viewMode === 'grid' ? 'bg-amber-100 text-amber-600' : 'text-gray-600'}`}
                            >
                                <Grid className="w-5 h-5" />
                            </button>
                            <button
                                onClick={() => setViewMode('list')}
                                className={`p-2 rounded ${viewMode === 'list' ? 'bg-amber-100 text-amber-600' : 'text-gray-600'}`}
                            >
                                <List className="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                    {/* Filters */}
                    <div className="flex flex-wrap gap-4">
                        <select
                            value={filterCuisine}
                            onChange={(e) => setFilterCuisine(e.target.value)}
                            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                        >
                            <option value="All">All Cuisines</option>
                            {cuisineTypes.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>

                        <select
                            value={filterMealType}
                            onChange={(e) => setFilterMealType(e.target.value)}
                            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                        >
                            <option value="All">All Meal Types</option>
                            {mealTypes.map(m => <option key={m} value={m}>{m}</option>)}
                        </select>

                        <select
                            value={filterDifficulty}
                            onChange={(e) => setFilterDifficulty(e.target.value)}
                            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                        >
                            <option value="All">All Difficulty Levels</option>
                            {difficulties.map(d => <option key={d} value={d}>{d}</option>)}
                        </select>

                        <button
                            onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
                            className={`px-4 py-2 rounded-lg font-medium transition-all ${
                                showFavoritesOnly 
                                    ? 'bg-red-100 text-red-600 border-2 border-red-300' 
                                    : 'bg-gray-100 text-gray-600 border-2 border-gray-300'
                            }`}
                        >
                            <Heart className={`w-4 h-4 inline mr-2 ${showFavoritesOnly ? 'fill-current' : ''}`} />
                            Favorites Only
                        </button>
                    </div>
                </div>

                {/* Recipe Count */}
                <div className="mb-4 text-gray-600">
                    {filteredRecipes.length} recipe{filteredRecipes.length !== 1 ? 's' : ''} found
                </div>

                {/* Recipes Grid/List */}
                {filteredRecipes.length === 0 ? (
                    <div className="bg-white rounded-lg shadow-md p-12 text-center">
                        <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500 text-lg">No recipes found</p>
                        <button
                            onClick={() => setShowAddRecipeModal(true)}
                            className="mt-4 px-6 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
                        >
                            Add Your First Recipe
                        </button>
                    </div>
                ) : viewMode === 'grid' ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredRecipes.map(recipe => (
                            <div key={recipe.id} className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow overflow-hidden">
                                {/* Recipe Image */}
                                <div className="h-48 bg-gradient-to-br from-amber-100 to-orange-100 relative">
                                    {recipe.photoUrl ? (
                                        <img src={recipe.photoUrl} alt={recipe.title} className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center">
                                            <Utensils className="w-20 h-20 text-amber-300" />
                                        </div>
                                    )}
                                    <button
                                        onClick={() => handleToggleFavorite(recipe.id)}
                                        className="absolute top-3 right-3 p-2 bg-white rounded-full shadow-md hover:scale-110 transition-transform"
                                    >
                                        <Heart className={`w-5 h-5 ${recipe.isFamilyFavorite ? 'fill-red-500 text-red-500' : 'text-gray-400'}`} />
                                    </button>
                                </div>

                                {/* Recipe Info */}
                                <div className="p-4">
                                    <h3 className="text-xl font-bold text-gray-900 mb-2">{recipe.title}</h3>
                                    <p className="text-sm text-gray-600 line-clamp-2 mb-3">{recipe.description}</p>

                                    {/* Badges */}
                                    <div className="flex flex-wrap gap-2 mb-3">
                                        {recipe.isFamilyFavorite && (
                                            <span className="px-2 py-1 bg-red-100 text-red-600 text-xs rounded-full flex items-center gap-1">
                                                <Crown className="w-3 h-3" />
                                                Family Favorite
                                            </span>
                                        )}
                                        {recipe.isHealthy && (
                                            <span className="px-2 py-1 bg-green-100 text-green-600 text-xs rounded-full flex items-center gap-1">
                                                <Leaf className="w-3 h-3" />
                                                Healthy
                                            </span>
                                        )}
                                        {recipe.isTraditional && (
                                            <span className="px-2 py-1 bg-amber-100 text-amber-600 text-xs rounded-full flex items-center gap-1">
                                                <Award className="w-3 h-3" />
                                                Traditional
                                            </span>
                                        )}
                                    </div>

                                    {/* Meta Info */}
                                    <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                                        <span className="flex items-center gap-1">
                                            <Clock className="w-4 h-4" />
                                            {parseInt(recipe.prepTime) + parseInt(recipe.cookTime)}m
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <Flame className={`w-4 h-4 ${recipe.difficulty === 'Easy' ? 'text-green-500' : recipe.difficulty === 'Hard' ? 'text-red-500' : 'text-amber-500'}`} />
                                            {recipe.difficulty}
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                                            {getAverageRating(recipe) || 'New'}
                                        </span>
                                    </div>

                                    {/* Actions */}
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => {
                                                setSelectedRecipe(recipe);
                                                setShowRecipeDetailModal(true);
                                            }}
                                            className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 font-medium"
                                        >
                                            View Recipe
                                        </button>
                                        <button
                                            onClick={() => {
                                                setEditingRecipe(recipe);
                                                setNewRecipe(recipe);
                                                setShowAddRecipeModal(true);
                                            }}
                                            className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                                        >
                                            <Edit className="w-5 h-5 text-gray-600" />
                                        </button>
                                        <button
                                            onClick={() => handleDeleteRecipe(recipe.id)}
                                            className="p-2 border border-gray-300 rounded-lg hover:bg-red-50"
                                        >
                                            <Trash2 className="w-5 h-5 text-red-600" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="space-y-4">
                        {filteredRecipes.map(recipe => (
                            <div key={recipe.id} className="bg-white rounded-lg shadow-md p-4 flex gap-4 hover:shadow-lg transition-shadow">
                                <div className="w-24 h-24 bg-gradient-to-br from-amber-100 to-orange-100 rounded-lg flex-shrink-0">
                                    {recipe.photoUrl ? (
                                        <img src={recipe.photoUrl} alt={recipe.title} className="w-full h-full object-cover rounded-lg" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center">
                                            <Utensils className="w-10 h-10 text-amber-300" />
                                        </div>
                                    )}
                                </div>
                                <div className="flex-1">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <h3 className="text-lg font-bold text-gray-900">{recipe.title}</h3>
                                            <p className="text-sm text-gray-600 line-clamp-1">{recipe.description}</p>
                                        </div>
                                        <button
                                            onClick={() => handleToggleFavorite(recipe.id)}
                                            className="p-2 hover:bg-gray-100 rounded-full"
                                        >
                                            <Heart className={`w-5 h-5 ${recipe.isFamilyFavorite ? 'fill-red-500 text-red-500' : 'text-gray-400'}`} />
                                        </button>
                                    </div>
                                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                                        <span>{recipe.cuisineType}</span>
                                        <span>•</span>
                                        <span>{recipe.difficulty}</span>
                                        <span>•</span>
                                        <span>{parseInt(recipe.prepTime) + parseInt(recipe.cookTime)} mins</span>
                                        <span>•</span>
                                        <span className="flex items-center gap-1">
                                            <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                                            {getAverageRating(recipe) || 'New'}
                                        </span>
                                    </div>
                                </div>
                                <div className="flex flex-col gap-2">
                                    <button
                                        onClick={() => {
                                            setSelectedRecipe(recipe);
                                            setShowRecipeDetailModal(true);
                                        }}
                                        className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm"
                                    >
                                        View
                                    </button>
                                    <button
                                        onClick={() => {
                                            setEditingRecipe(recipe);
                                            setNewRecipe(recipe);
                                            setShowAddRecipeModal(true);
                                        }}
                                        className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
                                    >
                                        Edit
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Add/Edit Recipe Modal */}
            {showAddRecipeModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
                    <div className="bg-white rounded-lg max-w-4xl w-full max-h-screen overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                            <h2 className="text-2xl font-bold">{editingRecipe ? 'Edit Recipe' : 'Add New Recipe'}</h2>
                            <button
                                onClick={() => {
                                    setShowAddRecipeModal(false);
                                    setEditingRecipe(null);
                                    resetRecipeForm();
                                }}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="p-6 space-y-6">
                            {/* Photo Upload */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Recipe Photo</label>
                                <input
                                    ref={photoInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handlePhotoSelect}
                                    className="hidden"
                                />
                                <div className="flex items-center space-x-4">
                                    {newRecipe.photoUrl ? (
                                        <img src={newRecipe.photoUrl} alt="Recipe" className="w-32 h-32 rounded-lg object-cover" />
                                    ) : (
                                        <div className="w-32 h-32 rounded-lg bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center">
                                            <Utensils className="w-16 h-16 text-amber-300" />
                                        </div>
                                    )}
                                    <button
                                        onClick={() => photoInputRef.current?.click()}
                                        className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg border border-gray-300"
                                    >
                                        Choose Photo
                                    </button>
                                </div>
                            </div>

                            {/* Basic Info */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Recipe Title *</label>
                                    <input
                                        type="text"
                                        value={newRecipe.title}
                                        onChange={(e) => setNewRecipe({ ...newRecipe, title: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                                        placeholder="e.g., Grandma's Special Biryani"
                                    />
                                </div>

                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                    <textarea
                                        value={newRecipe.description}
                                        onChange={(e) => setNewRecipe({ ...newRecipe, description: e.target.value })}
                                        rows={3}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                                        placeholder="Brief description of the recipe..."
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Prep Time (minutes)</label>
                                    <input
                                        type="number"
                                        value={newRecipe.prepTime}
                                        onChange={(e) => setNewRecipe({ ...newRecipe, prepTime: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                                        placeholder="15"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Cook Time (minutes)</label>
                                    <input
                                        type="number"
                                        value={newRecipe.cookTime}
                                        onChange={(e) => setNewRecipe({ ...newRecipe, cookTime: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                                        placeholder="30"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Servings</label>
                                    <input
                                        type="text"
                                        value={newRecipe.servings}
                                        onChange={(e) => setNewRecipe({ ...newRecipe, servings: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                                        placeholder="4 people"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Cuisine Type</label>
                                    <select
                                        value={newRecipe.cuisineType}
                                        onChange={(e) => setNewRecipe({ ...newRecipe, cuisineType: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                                    >
                                        {cuisineTypes.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Difficulty</label>
                                    <select
                                        value={newRecipe.difficulty}
                                        onChange={(e) => setNewRecipe({ ...newRecipe, difficulty: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                                    >
                                        {difficulties.map(d => <option key={d} value={d}>{d}</option>)}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Meal Type</label>
                                    <select
                                        value={newRecipe.mealType}
                                        onChange={(e) => setNewRecipe({ ...newRecipe, mealType: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                                    >
                                        {mealTypes.map(m => <option key={m} value={m}>{m}</option>)}
                                    </select>
                                </div>
                            </div>

                            {/* Badges */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Recipe Tags</label>
                                <div className="flex flex-wrap gap-3">
                                    <label className="flex items-center space-x-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={newRecipe.isFamilyFavorite}
                                            onChange={(e) => setNewRecipe({ ...newRecipe, isFamilyFavorite: e.target.checked })}
                                            className="w-4 h-4 text-red-600 rounded"
                                        />
                                        <span className="text-sm">Family Favorite</span>
                                    </label>
                                    <label className="flex items-center space-x-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={newRecipe.isHealthy}
                                            onChange={(e) => setNewRecipe({ ...newRecipe, isHealthy: e.target.checked })}
                                            className="w-4 h-4 text-green-600 rounded"
                                        />
                                        <span className="text-sm">Healthy Choice</span>
                                    </label>
                                    <label className="flex items-center space-x-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={newRecipe.isTraditional}
                                            onChange={(e) => setNewRecipe({ ...newRecipe, isTraditional: e.target.checked })}
                                            className="w-4 h-4 text-amber-600 rounded"
                                        />
                                        <span className="text-sm">Traditional Dish</span>
                                    </label>
                                </div>
                            </div>

                            {/* Ingredients */}
                            <div>
                                <div className="flex justify-between items-center mb-3">
                                    <label className="block text-sm font-medium text-gray-700">Ingredients</label>
                                    <button
                                        onClick={addIngredient}
                                        className="text-sm px-3 py-1 bg-amber-100 text-amber-700 rounded hover:bg-amber-200"
                                    >
                                        + Add Ingredient
                                    </button>
                                </div>
                                <div className="space-y-2 max-h-64 overflow-y-auto">
                                    {newRecipe.ingredients.map((ing, index) => (
                                        <div key={index} className="flex gap-2 items-start p-2 bg-gray-50 rounded">
                                            <input
                                                type="text"
                                                value={ing.name}
                                                onChange={(e) => updateIngredient(index, 'name', e.target.value)}
                                                className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                                                placeholder="Ingredient name"
                                            />
                                            <input
                                                type="text"
                                                value={ing.quantity}
                                                onChange={(e) => updateIngredient(index, 'quantity', e.target.value)}
                                                className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                                                placeholder="Qty"
                                            />
                                            <select
                                                value={ing.unit}
                                                onChange={(e) => updateIngredient(index, 'unit', e.target.value)}
                                                className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                                            >
                                                <option value="">Unit</option>
                                                {units.map(u => <option key={u} value={u}>{u}</option>)}
                                            </select>
                                            <label className="flex items-center space-x-1">
                                                <input
                                                    type="checkbox"
                                                    checked={ing.optional}
                                                    onChange={(e) => updateIngredient(index, 'optional', e.target.checked)}
                                                    className="w-3 h-3"
                                                />
                                                <span className="text-xs text-gray-600">Optional</span>
                                            </label>
                                            {newRecipe.ingredients.length > 1 && (
                                                <button
                                                    onClick={() => removeIngredient(index)}
                                                    className="text-red-600 hover:text-red-800"
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Instructions */}
                            <div>
                                <div className="flex justify-between items-center mb-3">
                                    <label className="block text-sm font-medium text-gray-700">Instructions</label>
                                    <button
                                        onClick={addInstruction}
                                        className="text-sm px-3 py-1 bg-amber-100 text-amber-700 rounded hover:bg-amber-200"
                                    >
                                        + Add Step
                                    </button>
                                </div>
                                <div className="space-y-2 max-h-64 overflow-y-auto">
                                    {newRecipe.instructions.map((inst, index) => (
                                        <div key={index} className="flex gap-2 items-start">
                                            <span className="text-sm font-semibold text-gray-600 mt-2">{index + 1}.</span>
                                            <textarea
                                                value={inst}
                                                onChange={(e) => updateInstruction(index, e.target.value)}
                                                rows={2}
                                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                                                placeholder="Describe this step..."
                                            />
                                            {newRecipe.instructions.length > 1 && (
                                                <button
                                                    onClick={() => removeInstruction(index)}
                                                    className="text-red-600 hover:text-red-800 mt-2"
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Optional Fields - Collapsible */}
                            <div className="border-t pt-4">
                                <details className="group">
                                    <summary className="cursor-pointer font-medium text-gray-700 flex items-center justify-between">
                                        <span>Nutrition Info (Optional)</span>
                                        <ChevronDown className="w-5 h-5 group-open:rotate-180 transition-transform" />
                                    </summary>
                                    <div className="mt-4 grid grid-cols-2 gap-4">
                                        <input
                                            type="text"
                                            value={newRecipe.nutritionInfo.calories}
                                            onChange={(e) => setNewRecipe({ ...newRecipe, nutritionInfo: { ...newRecipe.nutritionInfo, calories: e.target.value }})}
                                            className="px-3 py-2 border border-gray-300 rounded-lg"
                                            placeholder="Calories"
                                        />
                                        <input
                                            type="text"
                                            value={newRecipe.nutritionInfo.protein}
                                            onChange={(e) => setNewRecipe({ ...newRecipe, nutritionInfo: { ...newRecipe.nutritionInfo, protein: e.target.value }})}
                                            className="px-3 py-2 border border-gray-300 rounded-lg"
                                            placeholder="Protein (g)"
                                        />
                                        <input
                                            type="text"
                                            value={newRecipe.nutritionInfo.carbs}
                                            onChange={(e) => setNewRecipe({ ...newRecipe, nutritionInfo: { ...newRecipe.nutritionInfo, carbs: e.target.value }})}
                                            className="px-3 py-2 border border-gray-300 rounded-lg"
                                            placeholder="Carbs (g)"
                                        />
                                        <input
                                            type="text"
                                            value={newRecipe.nutritionInfo.fats}
                                            onChange={(e) => setNewRecipe({ ...newRecipe, nutritionInfo: { ...newRecipe.nutritionInfo, fats: e.target.value }})}
                                            className="px-3 py-2 border border-gray-300 rounded-lg"
                                            placeholder="Fats (g)"
                                        />
                                        <div className="col-span-2">
                                            <input
                                                type="text"
                                                value={newRecipe.nutritionInfo.allergens}
                                                onChange={(e) => setNewRecipe({ ...newRecipe, nutritionInfo: { ...newRecipe.nutritionInfo, allergens: e.target.value }})}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                placeholder="Allergens (e.g., nuts, dairy)"
                                            />
                                        </div>
                                    </div>
                                </details>
                            </div>

                            <div className="border-t pt-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Recipe Origin / Story (Optional)</label>
                                <textarea
                                    value={newRecipe.recipeOrigin}
                                    onChange={(e) => setNewRecipe({ ...newRecipe, recipeOrigin: e.target.value })}
                                    rows={3}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                                    placeholder="Share the story or history behind this recipe..."
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Healthy Swaps (Optional)</label>
                                <textarea
                                    value={newRecipe.healthySwaps}
                                    onChange={(e) => setNewRecipe({ ...newRecipe, healthySwaps: e.target.value })}
                                    rows={2}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                                    placeholder="e.g., Replace sugar with honey, use olive oil instead of butter..."
                                />
                            </div>
                        </div>

                        <div className="sticky bottom-0 bg-gray-50 px-6 py-4 flex justify-end space-x-3 border-t border-gray-200">
                            <button
                                onClick={() => {
                                    setShowAddRecipeModal(false);
                                    setEditingRecipe(null);
                                    resetRecipeForm();
                                }}
                                className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={editingRecipe ? handleUpdateRecipe : handleAddRecipe}
                                className="px-6 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 font-medium"
                            >
                                {editingRecipe ? 'Update' : 'Add'} Recipe
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Recipe Detail Modal - Coming Soon */}
            
            {/* Premium Modal */}
            {showPremiumModal && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black bg-opacity-75">
                    <div className="relative rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col" style={{ background: 'transparent' }}>
                        {/* Close Button */}
                        <button
                            onClick={() => setShowPremiumModal(false)}
                            className="absolute top-4 right-4 z-50 p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white transition-colors"
                        >
                            <X className="w-6 h-6 text-gray-600" />
                        </button>
                        
                        {/* Premium Component */}
                        <Premium isModal={true} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default RecipeBox;

